import Vue, { Component, CreateElement, RenderContext, VNode, VNodeChildren } from "vue";
import { FormField, FormValue } from "@/types";
import { VAutocomplete, VCheckbox, VTextarea, VTextField, VRadio, VRadioGroup } from "vuetify/lib";
import InputPassword from "../InputPassword.vue";
import BaseSelect from "../BaseSelect";
import { getFieldProps, mergeProps } from "@/lib/helpers/fieldHelpers";

interface Props {
  field: FormField;
  value: FormValue;
  fieldError: string[] | undefined;
}

export default Vue.extend({
  functional: true,
  name: "FieldPrinter",
  render(h: CreateElement, ctx: RenderContext<Props>): VNode {
    const field = ctx.props.field;
    let clazz = "";
    let childs: VNodeChildren = [];

    let props = getFieldProps(ctx.props.field, ctx.props.value);

    props.error = !!ctx.props.fieldError;
    props["error-messages"] = ctx.props.fieldError;

    if (ctx.scopedSlots.default) {
      const slotData = ctx.scopedSlots.default({
        field,
        listeners: ctx.listeners,
        value: props.value,
      });

      if (slotData && slotData.length === 1) {
        return slotData[0];
      } else if (slotData) return h("span", slotData);
    }

    let component: Component;
    if (field.type === "select") {
      component = BaseSelect;
      props.field = field;
    } else if (field.type === "autocomplete") {
      component = VAutocomplete;
      props.appendIcon = props.appendIcon || "";
      props = mergeProps(
        {
          appendIcon: "",
        },
        props
      );
    } else if (field.type === "checkbox") {
      component = VCheckbox;

      clazz = "my-0 py-0";

      props = mergeProps(
        {
          hideDetails: true,
          trueValue: true,
          inputValue: props.value,
          valueComparator: (current: any, trueValue: any) => !!current === !!trueValue,
        },
        props
      );
      ctx.listeners.change = ctx.listeners.input;
    } else if (field.type === "textarea") {
      component = VTextarea;
      props = mergeProps(
        {
          ["rows"]: 1,
          ["auto-grow"]: true,
        },
        props
      );
    } else if (field.type === "password") {
      component = InputPassword;
      props = {
        field: props,
        value: ctx.props.value,
        fieldError: ctx.props.fieldError,
      };
    } else if (field.type === "radio-group") {
      component = VRadioGroup;
      childs =
        field.list &&
        field.list.reduce((out, item) => {
          out.push(
            h(VRadio, {
              props: {
                label: item.text,
                value: item.value,
              },
            })
          );
          return out;
        }, [] as VNode[]);
      ctx.listeners.change = ctx.listeners.input;
    } else {
      component = VTextField;
    }

    return h(
      component,
      {
        props: {
          ...props,
          type: field.type,
        },
        class: clazz,
        on: ctx.listeners,
        attrs: { autocomplete: props.browserAutocomplete, name: field.name },
        ref: ctx.data.ref,
      },
      childs
    );
  },
});
